import { UserRole } from '../components/actions/ActionsPanel';
import { Action } from '../types/action';
import { Member } from '../types/member';
import { FinancialHealthStats } from '../types/financialStats';
import { v4 as uuidv4 } from 'uuid';

/**
 * Generate a list of actions based on the user's role and member data
 */
export const getActions = (
  role: UserRole,
  members: Member[],
  financialData: Record<string, FinancialHealthStats[]>
): Action[] => {
  if (members.length === 0 || Object.keys(financialData).length === 0) {
    return [];
  }

  let actions: Action[] = [];

  // Add role-specific actions
  if (role === 'CEO') {
    actions.push(...generateCEOActions(members, financialData));
  } else if (role === 'VP of Lending') {
    actions.push(...generateVPLendingActions(members, financialData));
  } else if (role === 'Loan Advisor') {
    actions.push(...generateLoanAdvisorActions(members, financialData));
  }

  // Shuffle the actions array to randomize the order
  const shuffledActions = [...actions].sort(() => Math.random() - 0.5);
  
  // Take a random subset (keeping at least 6-8 actions for variety)
  // This ensures that different actions appear each time the dashboard is loaded
  const minActions = Math.min(6, shuffledActions.length);
  const maxActions = Math.min(8, shuffledActions.length);
  const numActions = Math.floor(Math.random() * (maxActions - minActions + 1)) + minActions;
  
  return shuffledActions.slice(0, numActions);
};

/**
 * Generate actions for CEO role
 */
const generateCEOActions = (
  members: Member[],
  financialData: Record<string, FinancialHealthStats[]>
): Action[] => {
  const actions: Action[] = [];

  // 1. Portfolio Health Optimization
  const membersWithHealthIssues = findMembersWithHealthIssues(members, financialData);
  if (membersWithHealthIssues.length > 0) {
    actions.push({
      id: uuidv4(),
      title: 'Optimize Financial Health Across Portfolio',
      description: `Approve targeted financial health initiatives for ${membersWithHealthIssues.length} at-risk members`,
      impact: {
        type: 'financial',
        value: Math.round(membersWithHealthIssues.length * 3500),
        description: 'Projected revenue protection'
      },
      priority: 'high',
      category: 'Portfolio Management',
      applicableRoles: ['CEO'],
      actionType: 'semi-automated',
      dataSources: ['Financial Health Scores', 'Risk Models'],
      steps: [
        {
          id: uuidv4(),
          title: 'Review Financial Health Initiative',
          description: 'Review program details and performance projections',
          type: 'loan_review'
        },
        {
          id: uuidv4(),
          title: 'Approve Budget Allocation',
          description: 'Authorize resources for targeted intervention',
          type: 'account_update'
        },
        {
          id: uuidv4(),
          title: 'Schedule Executive Update',
          description: 'Set 30-day review of program effectiveness',
          type: 'notification'
        }
      ]
    });
  }
  
  // 2. Strategic Growth Opportunity
  actions.push({
    id: uuidv4(),
    title: 'Launch Home Equity Campaign',
    description: 'Approve targeted HELOC campaign to qualified homeowners with high equity',
    impact: {
      type: 'financial',
      value: 157000,
      description: 'Projected annual revenue'
    },
    priority: 'medium',
    category: 'Strategic Growth',
    applicableRoles: ['CEO'],
    actionType: 'semi-automated',
    dataSources: ['Property Values', 'Member Segmentation'],
    steps: [
      {
        id: uuidv4(),
        title: 'Review Campaign Parameters',
        description: 'Review target segments and qualification criteria',
        type: 'loan_review'
      },
      {
        id: uuidv4(),
        title: 'Approve Marketing Budget',
        description: 'Authorize campaign budget and resources',
        type: 'account_update'
      },
      {
        id: uuidv4(),
        title: 'Set Performance Targets',
        description: 'Define success metrics and monitoring framework',
        type: 'notification'
      }
    ]
  });
  
  // 3. Member Experience Enhancement
  actions.push({
    id: uuidv4(),
    title: 'Enable Personalized Financial Insights',
    description: 'Activate AI-powered personalized insights for all members',
    impact: {
      type: 'engagement',
      value: 34,
      description: 'Projected engagement increase'
    },
    priority: 'medium',
    category: 'Member Experience',
    applicableRoles: ['CEO'],
    actionType: 'automated',
    dataSources: ['Usage Analytics', 'Member Feedback'],
    steps: [
      {
        id: uuidv4(),
        title: 'Review Feature Overview',
        description: 'Review AI insights capabilities and member benefits',
        type: 'notification'
      },
      {
        id: uuidv4(),
        title: 'Approve Feature Launch',
        description: 'Authorize rollout to all member accounts',
        type: 'account_update'
      },
      {
        id: uuidv4(),
        title: 'Schedule Performance Review',
        description: 'Set 60-day review of feature adoption and impact',
        type: 'notification'
      }
    ]
  });

  // 4. Digital Transformation Initiative
  actions.push({
    id: uuidv4(),
    title: 'Accelerate Digital Banking Adoption',
    description: 'Approve digital acceleration strategy to boost mobile banking usage',
    impact: {
      type: 'financial',
      value: 215000,
      description: 'Cost reduction through digital channel shift'
    },
    priority: 'high',
    category: 'Digital Transformation',
    applicableRoles: ['CEO'],
    actionType: 'semi-automated',
    dataSources: ['Channel Analytics', 'Member Usage Data'],
    steps: [
      {
        id: uuidv4(),
        title: 'Review Adoption Strategy',
        description: 'Review digital acceleration plan and resource requirements',
        type: 'notification'
      },
      {
        id: uuidv4(),
        title: 'Approve Implementation Plan',
        description: 'Authorize phased implementation and resource allocation',
        type: 'account_update'
      },
      {
        id: uuidv4(),
        title: 'Monitor Channel Shift',
        description: 'Set up dashboard to track digital adoption metrics',
        type: 'notification'
      }
    ]
  });

  // 5. Competitive Response Strategy
  actions.push({
    id: uuidv4(),
    title: 'Counter Competitor Rate Campaign',
    description: 'Authorize targeted response to competitor\'s aggressive loan promotion',
    impact: {
      type: 'financial',
      value: 320000,
      description: 'Protected annual revenue'
    },
    priority: 'high',
    category: 'Competitive Strategy',
    applicableRoles: ['CEO'],
    actionType: 'manual',
    dataSources: ['Market Intelligence', 'Portfolio Risk Assessment'],
    steps: [
      {
        id: uuidv4(),
        title: 'Review Competitive Analysis',
        description: 'Review market impact assessment and response options',
        type: 'loan_review'
      },
      {
        id: uuidv4(),
        title: 'Approve Counter Strategy',
        description: 'Select and authorize targeted counter-campaign',
        type: 'account_update'
      },
      {
        id: uuidv4(),
        title: 'Brief Leadership Team',
        description: 'Communicate strategy to department heads',
        type: 'email'
      }
    ]
  });

  // 6. ESG Initiative
  actions.push({
    id: uuidv4(),
    title: 'Launch Green Lending Program',
    description: 'Approve new sustainability-focused loan products and goals',
    impact: {
      type: 'engagement',
      value: 28,
      description: 'Member satisfaction improvement'
    },
    priority: 'medium',
    category: 'Sustainability',
    applicableRoles: ['CEO'],
    actionType: 'manual',
    dataSources: ['Market Research', 'Member Surveys'],
    steps: [
      {
        id: uuidv4(),
        title: 'Review Program Proposal',
        description: 'Examine program structure and sustainability metrics',
        type: 'loan_review'
      },
      {
        id: uuidv4(),
        title: 'Approve Program Launch',
        description: 'Authorize implementation and marketing resources',
        type: 'account_update'
      },
      {
        id: uuidv4(),
        title: 'Schedule Board Presentation',
        description: 'Prepare ESG initiative update for board meeting',
        type: 'notification'
      }
    ]
  });

  // 7. Strategic Partnership
  actions.push({
    id: uuidv4(),
    title: 'Finalize Local Business Partnership',
    description: 'Approve joint program with chamber of commerce for small business support',
    impact: {
      type: 'financial',
      value: 185000,
      description: 'Projected new business loans'
    },
    priority: 'medium',
    category: 'Strategic Partnerships',
    applicableRoles: ['CEO'],
    actionType: 'manual',
    dataSources: ['Community Analysis', 'Business Loan Portfolio'],
    steps: [
      {
        id: uuidv4(),
        title: 'Review Partnership Terms',
        description: 'Examine final agreement details and resource commitments',
        type: 'loan_review'
      },
      {
        id: uuidv4(),
        title: 'Approve Agreement',
        description: 'Authorize partnership execution and implementation',
        type: 'account_update'
      },
      {
        id: uuidv4(),
        title: 'Schedule Launch Event',
        description: 'Plan official partnership announcement and kickoff',
        type: 'email'
      }
    ]
  });

  // 8. Talent Development
  actions.push({
    id: uuidv4(),
    title: 'Implement Leadership Development Program',
    description: 'Approve talent development initiative to strengthen leadership pipeline',
    impact: {
      type: 'engagement',
      value: 42,
      description: 'Employee retention improvement'
    },
    priority: 'medium',
    category: 'Talent Management',
    applicableRoles: ['CEO'],
    actionType: 'manual',
    dataSources: ['HR Analytics', 'Succession Planning Data'],
    steps: [
      {
        id: uuidv4(),
        title: 'Review Program Structure',
        description: 'Examine program design and success metrics',
        type: 'notification'
      },
      {
        id: uuidv4(),
        title: 'Approve Implementation',
        description: 'Authorize program launch and budget allocation',
        type: 'account_update'
      },
      {
        id: uuidv4(),
        title: 'Schedule Program Introduction',
        description: 'Plan leadership announcement and kickoff event',
        type: 'email'
      }
    ]
  });

  // 9. Merger & Acquisition Opportunity
  actions.push({
    id: uuidv4(),
    title: 'Evaluate Credit Union Acquisition',
    description: 'Assess potential acquisition of smaller regional credit union',
    impact: {
      type: 'financial',
      value: 1250000,
      description: 'Projected 5-year value creation'
    },
    priority: 'high',
    category: 'Strategic Growth',
    applicableRoles: ['CEO'],
    actionType: 'manual',
    dataSources: ['Market Analysis', 'Financial Due Diligence'],
    steps: [
      {
        id: uuidv4(),
        title: 'Review Acquisition Analysis',
        description: 'Examine detailed financial and strategic assessment',
        type: 'loan_review'
      },
      {
        id: uuidv4(),
        title: 'Approve Due Diligence',
        description: 'Authorize comprehensive due diligence process',
        type: 'account_update'
      },
      {
        id: uuidv4(),
        title: 'Schedule Board Discussion',
        description: 'Prepare board presentation for strategic discussion',
        type: 'notification'
      }
    ]
  });

  // 10. Technology Investment
  actions.push({
    id: uuidv4(),
    title: 'Upgrade Fraud Detection Systems',
    description: 'Approve investment in AI-powered fraud detection technology',
    impact: {
      type: 'financial',
      value: 430000,
      description: 'Annual fraud loss prevention'
    },
    priority: 'high',
    category: 'Risk Management',
    applicableRoles: ['CEO'],
    actionType: 'semi-automated',
    dataSources: ['Fraud Analytics', 'Technology Assessment'],
    steps: [
      {
        id: uuidv4(),
        title: 'Review Technology Proposal',
        description: 'Examine solution capabilities and implementation plan',
        type: 'notification'
      },
      {
        id: uuidv4(),
        title: 'Approve Technology Investment',
        description: 'Authorize budget and implementation timeline',
        type: 'account_update'
      },
      {
        id: uuidv4(),
        title: 'Set Performance Expectations',
        description: 'Define success metrics and ROI targets',
        type: 'notification'
      }
    ]
  });

  return actions;
};

/**
 * Generate actions for VP of Lending role
 * @param _members - Member data (reserved for future use)
 * @param _financialData - Financial data (reserved for future use)
 */
const generateVPLendingActions = (
  _members: Member[], // kept for consistency with other role generators
  _financialData: Record<string, FinancialHealthStats[]> // kept for consistency with other role generators
): Action[] => {
  const actions: Action[] = [];

  // 1. Auto Loan Rate Optimization
  actions.push({
    id: uuidv4(),
    title: 'Optimize Auto Loan Rate Strategy',
    description: 'Adjust auto loan rates based on market analysis and risk model updates',
    impact: {
      type: 'financial',
      value: 83500,
      description: 'Projected annual revenue increase'
    },
    priority: 'high',
    category: 'Lending Strategy',
    applicableRoles: ['VP of Lending'],
    actionType: 'semi-automated',
    dataSources: ['Market Rates', 'Portfolio Performance'],
    steps: [
      {
        id: uuidv4(),
        title: 'Review Rate Analysis',
        description: 'Review competitive analysis and portfolio performance data',
        type: 'loan_review'
      },
      {
        id: uuidv4(),
        title: 'Approve Rate Changes',
        description: 'Authorize updated rate structure for auto loans',
        type: 'account_update'
      },
      {
        id: uuidv4(),
        title: 'Notify Loan Officers',
        description: 'Communicate changes to lending team',
        type: 'email'
      }
    ]
  });
  
  // 2. Risk Management Alert
  const highRiskLoans = Math.floor(Math.random() * 15) + 5; // Mock data - would be calculated from real data
  actions.push({
    id: uuidv4(),
    title: 'Address Early Delinquency Signals',
    description: `Approve intervention for ${highRiskLoans} loans showing early warning signs`,
    impact: {
      type: 'financial',
      value: highRiskLoans * 12000,
      description: 'Projected loss prevention'
    },
    priority: 'high',
    category: 'Risk Management',
    applicableRoles: ['VP of Lending'],
    actionType: 'semi-automated',
    dataSources: ['Payment Patterns', 'Delinquency Models'],
    steps: [
      {
        id: uuidv4(),
        title: 'Review Risk Assessment',
        description: 'Review identified loans and risk factors',
        type: 'loan_review'
      },
      {
        id: uuidv4(),
        title: 'Approve Intervention Strategy',
        description: 'Authorize personalized outreach and assistance options',
        type: 'account_update'
      },
      {
        id: uuidv4(),
        title: 'Assign Team Resources',
        description: 'Allocate loan officer resources for outreach',
        type: 'notification'
      }
    ]
  });
  
  // 3. Product Opportunity
  actions.push({
    id: uuidv4(),
    title: 'Launch First-Time Homebuyer Program',
    description: 'Approve specialized mortgage program for first-time homebuyers',
    impact: {
      type: 'financial',
      value: 215000,
      description: 'Projected annual revenue'
    },
    priority: 'medium',
    category: 'Product Development',
    applicableRoles: ['VP of Lending'],
    actionType: 'manual',
    dataSources: ['Market Analysis', 'Member Demographics'],
    steps: [
      {
        id: uuidv4(),
        title: 'Review Program Details',
        description: 'Review program features, qualification criteria, and pricing',
        type: 'loan_review'
      },
      {
        id: uuidv4(),
        title: 'Approve Program Launch',
        description: 'Authorize program implementation and staff training',
        type: 'account_update'
      },
      {
        id: uuidv4(),
        title: 'Schedule Marketing Kickoff',
        description: 'Initiate marketing campaign for program awareness',
        type: 'email'
      }
    ]
  });

  // 4. Loan Officer Performance Optimization
  actions.push({
    id: uuidv4(),
    title: 'Implement Loan Officer Efficiency Program',
    description: 'Approve new workflow optimization to increase loan processing capacity',
    impact: {
      type: 'financial',
      value: 178500,
      description: 'Increased loan throughput value'
    },
    priority: 'high',
    category: 'Operational Efficiency',
    applicableRoles: ['VP of Lending'],
    actionType: 'semi-automated',
    dataSources: ['Process Analytics', 'Loan Officer Performance'],
    steps: [
      {
        id: uuidv4(),
        title: 'Review Efficiency Analysis',
        description: 'Examine workflow bottlenecks and improvement opportunities',
        type: 'loan_review'
      },
      {
        id: uuidv4(),
        title: 'Approve Implementation Plan',
        description: 'Authorize process changes and technology investments',
        type: 'account_update'
      },
      {
        id: uuidv4(),
        title: 'Schedule Team Training',
        description: 'Plan training sessions for loan processing staff',
        type: 'email'
      }
    ]
  });

  // 5. Credit Policy Update
  actions.push({
    id: uuidv4(),
    title: 'Update Personal Loan Credit Policy',
    description: 'Approve revised underwriting criteria for unsecured personal loans',
    impact: {
      type: 'financial',
      value: 125000,
      description: 'Risk-adjusted portfolio improvement'
    },
    priority: 'medium',
    category: 'Policy Management',
    applicableRoles: ['VP of Lending'],
    actionType: 'manual',
    dataSources: ['Portfolio Performance', 'Credit Risk Models'],
    steps: [
      {
        id: uuidv4(),
        title: 'Review Policy Changes',
        description: 'Examine proposed criteria updates and impact analysis',
        type: 'loan_review'
      },
      {
        id: uuidv4(),
        title: 'Approve Policy Update',
        description: 'Authorize implementation of new underwriting standards',
        type: 'account_update'
      },
      {
        id: uuidv4(),
        title: 'Communicate Changes',
        description: 'Share policy updates with lending and underwriting teams',
        type: 'email'
      }
    ]
  });

  // 6. Strategic Lending Partnership
  actions.push({
    id: uuidv4(),
    title: 'Finalize Indirect Auto Lending Partnership',
    description: 'Approve partnership with regional auto dealer network',
    impact: {
      type: 'financial',
      value: 350000,
      description: 'Projected annual loan volume'
    },
    priority: 'high',
    category: 'Strategic Growth',
    applicableRoles: ['VP of Lending'],
    actionType: 'manual',
    dataSources: ['Market Analysis', 'Dealer Performance'],
    steps: [
      {
        id: uuidv4(),
        title: 'Review Partnership Terms',
        description: 'Examine final agreement details and performance projections',
        type: 'loan_review'
      },
      {
        id: uuidv4(),
        title: 'Approve Final Agreement',
        description: 'Authorize execution of dealer partnership agreements',
        type: 'account_update'
      },
      {
        id: uuidv4(),
        title: 'Schedule Implementation',
        description: 'Plan technical integration and training timeline',
        type: 'email'
      }
    ]
  });

  // 7. Risk-based Pricing Implementation
  actions.push({
    id: uuidv4(),
    title: 'Implement Dynamic Risk-Based Pricing',
    description: 'Approve enhanced pricing model for consumer loans based on risk tiers',
    impact: {
      type: 'financial',
      value: 267000,
      description: 'Projected annual portfolio yield improvement'
    },
    priority: 'medium',
    category: 'Pricing Strategy',
    applicableRoles: ['VP of Lending'],
    actionType: 'semi-automated',
    dataSources: ['Risk Models', 'Portfolio Performance'],
    steps: [
      {
        id: uuidv4(),
        title: 'Review Pricing Model',
        description: 'Examine tiered pricing structure and risk correlation',
        type: 'loan_review'
      },
      {
        id: uuidv4(),
        title: 'Approve Implementation',
        description: 'Authorize system updates and new pricing guidelines',
        type: 'account_update'
      },
      {
        id: uuidv4(),
        title: 'Train Lending Teams',
        description: 'Schedule staff training on new pricing approach',
        type: 'email'
      }
    ]
  });

  // 8. Portfolio Stress Test
  actions.push({
    id: uuidv4(),
    title: 'Review Quarterly Loan Portfolio Stress Test',
    description: 'Assess portfolio resilience under adverse economic scenarios',
    impact: {
      type: 'financial',
      value: 520000,
      description: 'Potential loss mitigation'
    },
    priority: 'high',
    category: 'Risk Management',
    applicableRoles: ['VP of Lending'],
    actionType: 'semi-automated',
    dataSources: ['Economic Indicators', 'Portfolio Analytics'],
    steps: [
      {
        id: uuidv4(),
        title: 'Review Stress Test Results',
        description: 'Examine portfolio vulnerabilities and capital adequacy',
        type: 'loan_review'
      },
      {
        id: uuidv4(),
        title: 'Approve Risk Mitigation Actions',
        description: 'Authorize preventative measures for identified vulnerabilities',
        type: 'account_update'
      },
      {
        id: uuidv4(),
        title: 'Report to ALCO',
        description: 'Prepare summary for Asset-Liability Committee review',
        type: 'notification'
      }
    ]
  });

  // 9. Mortgage Process Optimization
  actions.push({
    id: uuidv4(),
    title: 'Streamline Mortgage Approval Process',
    description: 'Approve new digital mortgage workflow to reduce approval time by 40%',
    impact: {
      type: 'engagement',
      value: 45,
      description: 'Member satisfaction improvement'
    },
    priority: 'medium',
    category: 'Process Improvement',
    applicableRoles: ['VP of Lending'],
    actionType: 'semi-automated',
    dataSources: ['Process Analytics', 'Member Feedback'],
    steps: [
      {
        id: uuidv4(),
        title: 'Review Process Redesign',
        description: 'Examine workflow changes and technology integration',
        type: 'loan_review'
      },
      {
        id: uuidv4(),
        title: 'Approve Implementation',
        description: 'Authorize system updates and process changes',
        type: 'account_update'
      },
      {
        id: uuidv4(),
        title: 'Monitor Performance Metrics',
        description: 'Set up dashboard to track approval times and experience',
        type: 'notification'
      }
    ]
  });

  // 10. Loan Growth Campaign
  actions.push({
    id: uuidv4(),
    title: 'Launch Summer Loan Growth Campaign',
    description: 'Approve targeted campaign to increase personal loan originations',
    impact: {
      type: 'financial',
      value: 195000,
      description: 'Projected campaign revenue'
    },
    priority: 'medium',
    category: 'Marketing Strategy',
    applicableRoles: ['VP of Lending'],
    actionType: 'semi-automated',
    dataSources: ['Member Segmentation', 'Campaign Analytics'],
    steps: [
      {
        id: uuidv4(),
        title: 'Review Campaign Strategy',
        description: 'Examine target segments, offers, and projected results',
        type: 'loan_review'
      },
      {
        id: uuidv4(),
        title: 'Approve Campaign Budget',
        description: 'Authorize marketing spend and incentive structure',
        type: 'account_update'
      },
      {
        id: uuidv4(),
        title: 'Brief Lending Team',
        description: 'Communicate campaign details to front-line staff',
        type: 'email'
      }
    ]
  });

  return actions;
};

/**
 * Generate actions for Loan Advisor role
 */
const generateLoanAdvisorActions = (
  members: Member[],
  financialData: Record<string, FinancialHealthStats[]>
): Action[] => {
  const actions: Action[] = [];
  
  // Find members with appropriate opportunities
  const membersWithOpportunities = findMembersWithOpportunities(members, financialData);
  
  // Debug logging
  if (import.meta.env.DEV) console.log(`Found ${membersWithOpportunities.length} members with opportunities and English names`);
  
  if (membersWithOpportunities.length > 0) {
    // Take the first 3 members for individual actions
    membersWithOpportunities.slice(0, 3).forEach(member => {
      // 1. Auto Loan Refinance Opportunity
      if (Math.random() > 0.5) { // Randomly determine if this member has this opportunity
        actions.push({
          id: uuidv4(),
          title: `Auto Loan Refinance for ${member.first_name} ${member.last_name}`,
          description: 'Offer auto loan refinance at lower rate based on improved credit score',
          impact: {
            type: 'financial',
            value: 2340,
            description: 'Member lifetime value increase'
          },
          priority: 'medium',
          category: 'Refinance Opportunity',
          applicableRoles: ['Loan Advisor'],
          memberId: member.uuid,
          memberName: `${member.first_name} ${member.last_name}`,
          memberPhoto: member.photo,
          actionType: 'semi-automated',
          dataSources: ['Credit Score', 'Loan Terms'],
          steps: [
            {
              id: uuidv4(),
              title: 'Review Member Profile',
              description: 'Review current loan and qualification for better terms',
              type: 'loan_review'
            },
            {
              id: uuidv4(),
              title: 'Prepare Personalized Offer',
              description: 'Generate custom refinance offer with calculated savings',
              type: 'account_update'
            },
            {
              id: uuidv4(),
              title: 'Send Offer to Member',
              description: 'Contact member with personalized refinance opportunity',
              type: 'email'
            }
          ]
        });
      } else {
        // 2. Financial Health Coaching Session
        actions.push({
          id: uuidv4(),
          title: `Financial Health Review for ${member.first_name} ${member.last_name}`,
          description: 'Schedule financial health review to address declining cash reserves',
          impact: {
            type: 'engagement',
            value: 83,
            description: 'Retention probability'
          },
          priority: 'high',
          category: 'Financial Wellness',
          applicableRoles: ['Loan Advisor'],
          memberId: member.uuid,
          memberName: `${member.first_name} ${member.last_name}`,
          memberPhoto: member.photo,
          actionType: 'manual',
          dataSources: ['Financial Health Score', 'Cash Flow Analysis'],
          steps: [
            {
              id: uuidv4(),
              title: 'Review Financial Status',
              description: 'Analyze current financial health indicators and trends',
              type: 'loan_review'
            },
            {
              id: uuidv4(),
              title: 'Prepare Coaching Materials',
              description: 'Generate personalized guidance and resource recommendations',
              type: 'account_update'
            },
            {
              id: uuidv4(),
              title: 'Schedule Coaching Session',
              description: 'Set up meeting with member for financial health review',
              type: 'email'
            }
          ]
        });
      }
    });
  }

  // 3. Member Milestone Recognition
  const englishNamedMembers = members.filter(member => {
    // Check for English character names
    const nameChars = ((member.first_name || '') + (member.last_name || '')).toLowerCase();
    return /^[a-z\s\-'\.]+$/i.test(nameChars);
  });
  
  if (englishNamedMembers.length > 0) {
    const memberIndex = Math.floor(Math.random() * englishNamedMembers.length);
    const member = englishNamedMembers[memberIndex];
    actions.push({
      id: uuidv4(),
      title: `Celebrate ${member.first_name}'s Debt Reduction Milestone`,
      description: 'Recognize significant debt reduction achievement with personalized message',
      impact: {
        type: 'engagement',
        value: 95,
        description: 'Satisfaction score impact'
      },
      priority: 'medium',
      category: 'Member Recognition',
      applicableRoles: ['Loan Advisor'],
      memberId: member.uuid,
      memberName: `${member.first_name} ${member.last_name}`,
      memberPhoto: member.photo,
      actionType: 'automated',
      dataSources: ['Payment History', 'Debt Reduction Tracking'],
      steps: [
        {
          id: uuidv4(),
          title: 'Review Achievement Details',
          description: 'Verify debt reduction milestone and achievement metrics',
          type: 'loan_review'
        },
        {
          id: uuidv4(),
          title: 'Personalize Recognition Message',
          description: 'Customize congratulatory message with personal touches',
          type: 'notification'
        },
        {
          id: uuidv4(),
          title: 'Send Milestone Recognition',
          description: 'Deliver congratulatory message with achievement badge',
          type: 'email'
        }
      ]
    });
  }

  // If no actions were generated (e.g., no English-named members), add a generic action
  if (actions.length === 0) {
    actions.push({
      id: uuidv4(),
      title: `Financial Health Campaign`,
      description: 'Launch targeted outreach to members with declining financial health scores',
      impact: {
        type: 'engagement',
        value: 78,
        description: 'Member retention increase'
      },
      priority: 'high',
      category: 'Financial Wellness',
      applicableRoles: ['Loan Advisor'],
      actionType: 'semi-automated',
      dataSources: ['Financial Health Scores', 'Member Engagement Data'],
      steps: [
        {
          id: uuidv4(),
          title: 'Review Campaign Parameters',
          description: 'Review target segments and outreach criteria',
          type: 'loan_review'
        },
        {
          id: uuidv4(),
          title: 'Prepare Outreach Materials',
          description: 'Generate customized communication templates',
          type: 'account_update'
        },
        {
          id: uuidv4(),
          title: 'Schedule Member Contacts',
          description: 'Set up follow-up schedule with prioritized members',
          type: 'email'
        }
      ]
    });
  }
  
  return actions;
};

/**
 * Find members with financial health issues
 */
const findMembersWithHealthIssues = (
  members: Member[],
  financialData: Record<string, FinancialHealthStats[]>
): Member[] => {
  return members.filter(member => {
    const memberFinancialData = financialData[member.uuid];
    if (!memberFinancialData || memberFinancialData.length === 0) {
      return false;
    }
    
    // Get the most recent financial data
    const latestData = memberFinancialData[0];
    
    // Check if they have health issues - would use more sophisticated logic in production
    const hasHealthIssues = 
      (latestData.verascore_health === 'AT_RISK' || 
       latestData.verascore_health === 'NEEDS_IMPROVEMENT' ||
       latestData.verascore_health === 'At Risk' || 
       latestData.verascore_health === 'Needs Improvement') ||
      (latestData.dti_health === 'AT_RISK' || 
       latestData.dti_health === 'NEEDS_IMPROVEMENT' ||
       latestData.dti_health === 'At Risk' || 
       latestData.dti_health === 'Needs Improvement') ||
      (latestData.cash_lr_health === 'AT_RISK' || 
       latestData.cash_lr_health === 'NEEDS_IMPROVEMENT' ||
       latestData.cash_lr_health === 'At Risk' || 
       latestData.cash_lr_health === 'Needs Improvement');
    
    return hasHealthIssues;
  });
};

/**
 * Find members with opportunities for loan advisors
 */
const findMembersWithOpportunities = (
  members: Member[],
  financialData: Record<string, FinancialHealthStats[]>
): Member[] => {
  // First filter for members with financial data
  const membersWithData = members.filter(member => {
    const memberFinancialData = financialData[member.uuid];
    return memberFinancialData && memberFinancialData.length > 0;
  });
  
  // Then filter for members with English names only (for demo purposes)
  const englishNamedMembers = membersWithData.filter(member => {
    // Check for English character names
    const nameChars = ((member.first_name || '') + (member.last_name || '')).toLowerCase();
    return /^[a-z\s\-'\.]+$/i.test(nameChars);
  });
  
  // Sort alphabetically by last name for consistency
  return englishNamedMembers.sort((a, b) => a.last_name.localeCompare(b.last_name));
};

/**
 * Generate a new action for refreshing in the CEO role
 */
export const generateNewCEOAction = (): Action => {
  // CEO action types
  const actionTypes = ['strategic', 'growth', 'experience'];
  const selectedType = actionTypes[Math.floor(Math.random() * actionTypes.length)];
  
  if (selectedType === 'strategic') {
    return {
      id: uuidv4(),
      title: 'Optimize Financial Health Across Portfolio',
      description: 'Approve targeted financial health initiatives for at-risk members',
      impact: {
        type: 'financial',
        value: 127500,
        description: 'Projected revenue protection'
      },
      priority: 'high',
      category: 'Portfolio Management',
      applicableRoles: ['CEO'],
      actionType: 'semi-automated',
      dataSources: ['Financial Health Scores', 'Risk Models'],
      steps: [
        {
          id: uuidv4(),
          title: 'Review Financial Health Initiative',
          description: 'Review program details and performance projections',
          type: 'loan_review'
        },
        {
          id: uuidv4(),
          title: 'Approve Budget Allocation',
          description: 'Authorize resources for targeted intervention',
          type: 'account_update'
        },
        {
          id: uuidv4(),
          title: 'Schedule Executive Update',
          description: 'Set 30-day review of program effectiveness',
          type: 'notification'
        }
      ]
    };
  } else if (selectedType === 'growth') {
    return {
      id: uuidv4(),
      title: 'Launch Home Equity Campaign',
      description: 'Approve targeted HELOC campaign to qualified homeowners with high equity',
      impact: {
        type: 'financial',
        value: 157000,
        description: 'Projected annual revenue'
      },
      priority: 'medium',
      category: 'Strategic Growth',
      applicableRoles: ['CEO'],
      actionType: 'semi-automated',
      dataSources: ['Property Values', 'Member Segmentation'],
      steps: [
        {
          id: uuidv4(),
          title: 'Review Campaign Parameters',
          description: 'Review target segments and qualification criteria',
          type: 'loan_review'
        },
        {
          id: uuidv4(),
          title: 'Approve Marketing Budget',
          description: 'Authorize campaign budget and resources',
          type: 'account_update'
        },
        {
          id: uuidv4(),
          title: 'Set Performance Targets',
          description: 'Define success metrics and monitoring framework',
          type: 'notification'
        }
      ]
    };
  } else {
    return {
      id: uuidv4(),
      title: 'Enable Personalized Financial Insights',
      description: 'Activate AI-powered personalized insights for all members',
      impact: {
        type: 'engagement',
        value: 34,
        description: 'Projected engagement increase'
      },
      priority: 'medium',
      category: 'Member Experience',
      applicableRoles: ['CEO'],
      actionType: 'automated',
      dataSources: ['Usage Analytics', 'Member Feedback'],
      steps: [
        {
          id: uuidv4(),
          title: 'Review Feature Overview',
          description: 'Review AI insights capabilities and member benefits',
          type: 'notification'
        },
        {
          id: uuidv4(),
          title: 'Approve Feature Launch',
          description: 'Authorize rollout to all member accounts',
          type: 'account_update'
        },
        {
          id: uuidv4(),
          title: 'Schedule Performance Review',
          description: 'Set 60-day review of feature adoption and impact',
          type: 'notification'
        }
      ]
    };
  }
};

/**
 * Generate a new action for refreshing in the VP of Lending role
 */
export const generateNewVPLendingAction = (member: Member): Action => {
  // VP of Lending action types
  const actionTypes = ['risk', 'portfolio', 'program'];
  const selectedType = actionTypes[Math.floor(Math.random() * actionTypes.length)];
  
  if (selectedType === 'risk') {
    return {
      id: uuidv4(),
      title: `Risk Assessment for ${member.first_name}'s Loan`,
      description: 'Review loan showing early warning signs and authorize mitigation strategy',
      impact: {
        type: 'financial',
        value: 18500,
        description: 'Potential loss prevention'
      },
      priority: 'high',
      category: 'Risk Management',
      applicableRoles: ['VP of Lending'],
      memberId: member.uuid,
      memberName: `${member.first_name} ${member.last_name}`,
      actionType: 'semi-automated',
      dataSources: ['Payment Patterns', 'Credit Score Trends'],
      steps: [
        {
          id: uuidv4(),
          title: 'Review Risk Indicators',
          description: 'Analyze early warning signs and risk assessment',
          type: 'loan_review'
        },
        {
          id: uuidv4(),
          title: 'Approve Intervention Strategy',
          description: 'Select and authorize appropriate intervention approach',
          type: 'account_update'
        },
        {
          id: uuidv4(),
          title: 'Schedule Follow-up Review',
          description: 'Set timeline for effectiveness review',
          type: 'notification'
        }
      ]
    };
  } else if (selectedType === 'portfolio') {
    return {
      id: uuidv4(),
      title: 'Optimize Auto Loan Portfolio Mix',
      description: 'Adjust portfolio allocation based on performance insights',
      impact: {
        type: 'financial',
        value: 76300,
        description: 'Projected revenue increase'
      },
      priority: 'medium',
      category: 'Portfolio Management',
      applicableRoles: ['VP of Lending'],
      actionType: 'semi-automated',
      dataSources: ['Portfolio Performance', 'Market Analysis'],
      steps: [
        {
          id: uuidv4(),
          title: 'Review Portfolio Analysis',
          description: 'Examine performance data and optimization opportunities',
          type: 'loan_review'
        },
        {
          id: uuidv4(),
          title: 'Approve Allocation Changes',
          description: 'Authorize updated portfolio targets',
          type: 'account_update'
        },
        {
          id: uuidv4(),
          title: 'Communicate Strategy Update',
          description: 'Share new approach with lending team',
          type: 'email'
        }
      ]
    };
  } else {
    return {
      id: uuidv4(),
      title: 'Launch First-Time Homebuyer Program',
      description: 'Approve specialized mortgage program for first-time homebuyers',
      impact: {
        type: 'financial',
        value: 215000,
        description: 'Projected annual revenue'
      },
      priority: 'medium',
      category: 'Product Development',
      applicableRoles: ['VP of Lending'],
      actionType: 'manual',
      dataSources: ['Market Analysis', 'Member Demographics'],
      steps: [
        {
          id: uuidv4(),
          title: 'Review Program Details',
          description: 'Review program features, qualification criteria, and pricing',
          type: 'loan_review'
        },
        {
          id: uuidv4(),
          title: 'Approve Program Launch',
          description: 'Authorize program implementation and staff training',
          type: 'account_update'
        },
        {
          id: uuidv4(),
          title: 'Schedule Marketing Kickoff',
          description: 'Initiate marketing campaign for program awareness',
          type: 'email'
        }
      ]
    };
  }
};

/**
 * Generate a new action for refreshing in the Loan Advisor role
 */
export const generateNewLoanAdvisorAction = (member: Member): Action => {
  // Randomly choose between two action types
  const isRefinance = Math.random() > 0.5;
  
  if (isRefinance) {
    return {
      id: uuidv4(),
      title: `Auto Loan Refinance for ${member.first_name} ${member.last_name}`,
      description: 'Offer auto loan refinance at lower rate based on improved credit score',
      impact: {
        type: 'financial',
        value: 2340,
        description: 'Member lifetime value increase'
      },
      priority: 'medium',
      category: 'Refinance Opportunity',
      applicableRoles: ['Loan Advisor'],
      memberId: member.uuid,
      memberName: `${member.first_name} ${member.last_name}`,
      memberPhoto: member.photo,
      actionType: 'semi-automated',
      dataSources: ['Credit Score', 'Loan Terms'],
      steps: [
        {
          id: uuidv4(),
          title: 'Review Member Profile',
          description: 'Review current loan and qualification for better terms',
          type: 'loan_review'
        },
        {
          id: uuidv4(),
          title: 'Prepare Personalized Offer',
          description: 'Generate custom refinance offer with calculated savings',
          type: 'account_update'
        },
        {
          id: uuidv4(),
          title: 'Send Offer to Member',
          description: 'Contact member with personalized refinance opportunity',
          type: 'email'
        }
      ]
    };
  } else {
    return {
      id: uuidv4(),
      title: `Financial Health Review for ${member.first_name} ${member.last_name}`,
      description: 'Schedule financial health review to address declining cash reserves',
      impact: {
        type: 'engagement',
        value: 83,
        description: 'Retention probability'
      },
      priority: 'high',
      category: 'Financial Wellness',
      applicableRoles: ['Loan Advisor'],
      memberId: member.uuid,
      memberName: `${member.first_name} ${member.last_name}`,
      memberPhoto: member.photo,
      actionType: 'manual',
      dataSources: ['Financial Health Score', 'Cash Flow Analysis'],
      steps: [
        {
          id: uuidv4(),
          title: 'Review Financial Status',
          description: 'Analyze current financial health indicators and trends',
          type: 'loan_review'
        },
        {
          id: uuidv4(),
          title: 'Prepare Coaching Materials',
          description: 'Generate personalized guidance and resource recommendations',
          type: 'account_update'
        },
        {
          id: uuidv4(),
          title: 'Schedule Coaching Session',
          description: 'Set up meeting with member for financial health review',
          type: 'email'
        }
      ]
    };
  }
};

/**
 * Generate a new action for any role based on a member
 */
export const generateNewAction = (role: UserRole, member: Member): Action => {
  if (role === 'CEO') {
    return generateNewCEOAction();
  } else if (role === 'VP of Lending') {
    return generateNewVPLendingAction(member);
  } else if (role === 'Loan Advisor') {
    return generateNewLoanAdvisorAction(member);
  }
  
  // Default fallback action if role is not recognized
  return {
    id: uuidv4(),
    title: 'Review Financial Opportunity',
    description: 'Analyze new financial opportunity identified by the system',
    impact: {
      type: 'financial',
      value: 50000,
      description: 'Potential value'
    },
    priority: 'medium',
    category: 'Opportunity',
    applicableRoles: [role],
    actionType: 'manual',
    dataSources: ['System Analysis', 'Financial Data'],
    steps: [
      {
        id: uuidv4(),
        title: 'Review Opportunity Details',
        description: 'Analyze details and potential impact',
        type: 'notification'
      },
      {
        id: uuidv4(),
        title: 'Make Decision',
        description: 'Decide on next steps for this opportunity',
        type: 'account_update'
      },
      {
        id: uuidv4(),
        title: 'Document Outcome',
        description: 'Record decision and expected outcomes',
        type: 'notification'
      }
    ]
  };
};

// ============================================================================
// P2C: Data-Driven Behavioral Recommendations
// ============================================================================

/** P3+P4C: Behavioral insights summary from the lender insights API (aggregate only) */
export interface BehavioralInsights {
  high_financial_stress_count: number;
  dti_high_count: number;
  /** P4C: Members with VeraScore decline ≥ 15 points in 90 days */
  declining_score_90d_count: number;
  /** P4C-2B: Members with < 1 month liquid reserves */
  liquidity_months_critical_count: number;
}

// ─── P3: Rule Engine v1 ─────────────────────────────────────────────────────
// Each rule maps a set of trigger conditions (aggregate counts) to an Action
// template. Rules are evaluated independently; priority scoring determines
// final ordering.

/** Priority weight map — higher = more urgent */
const PRIORITY_WEIGHTS: Record<string, number> = {
  critical: 100,
  high: 75,
  medium: 50,
  low: 25,
};

interface RecommendationRule {
  key: string;
  /** Returns true when the rule should fire */
  condition: (b: BehavioralInsights) => boolean;
  /** Build the Action when the rule fires */
  build: (b: BehavioralInsights) => Action;
  /** Numeric priority for sorting (higher = first) */
  priorityWeight: number;
}

/**
 * P4A: Maps recommendation rule keys to their target segment_key.
 * These segment_keys reference vs_segments.segment_key in the database.
 * Used to populate Action.segmentKey for cohort-based outcome tracking.
 *
 * IMPORTANT: When adding new rules, you MUST add a mapping here or
 * the accepted event will not trigger baseline creation (P4A.5 guardrail).
 */
const RULE_SEGMENT_KEY_MAP: Record<string, string> = {
  HIGH_RISK_INTERVENTION: 'high_financial_stress',
  HIGH_FINANCIAL_STRESS_SUPPORT: 'high_financial_stress',
  HIGH_DTI_PROGRAM: 'dti_high',
  HIGH_DTI_STRESS: 'dti_high',
  LOW_LIQUIDITY_SUPPORT: 'liquidity_months_critical',
  LOW_LIQUID_RESERVES: 'liquidity_months_critical',
  SCORE_IMPROVEMENT_CAMPAIGN: 'vs_band_at_risk',
  DECLINING_SCORE_90D: 'declining_score_90d',
};

const RECOMMENDATION_RULES: RecommendationRule[] = [
  // ── Rule 1: High Risk Intervention (multi-signal: Stress AND DTI) ──────
  {
    key: 'HIGH_RISK_INTERVENTION',
    condition: (b) => b.high_financial_stress_count > 0 && b.dti_high_count > 0,
    priorityWeight: PRIORITY_WEIGHTS.critical,
    build: (b) => {
      const stressCount = b.high_financial_stress_count;
      const dtiCount = b.dti_high_count;
      return {
        id: 'behavioral-high-risk-intervention',
        title: 'High Risk Intervention Required',
        description:
          `${stressCount} member${stressCount !== 1 ? 's' : ''} report elevated financial stress AND ` +
          `${dtiCount} member${dtiCount !== 1 ? 's' : ''} have high debt-to-income ratios. ` +
          'This overlap signals urgent default risk — prioritize proactive outreach and hardship support.',
        impact: {
          type: 'risk',
          value: Math.max(stressCount, dtiCount),
          description: `Up to ${Math.max(stressCount, dtiCount)} members at elevated risk`,
        },
        priority: 'critical' as const,
        category: 'Risk Management',
        applicableRoles: ['CEO', 'VP of Lending', 'Loan Advisor'],
        actionType: 'semi-automated' as const,
        dataSources: ['VS Insights — Live Survey Data', 'VS Insights — DTI Segments'],
        steps: [
          {
            id: 'step-review-overlap',
            title: 'Review Risk Overlap',
            description: 'Open VS Insights → Financial Health to review stress and DTI KPIs together.',
            type: 'loan_review' as const,
          },
          {
            id: 'step-assess-exposure',
            title: 'Assess Portfolio Exposure',
            description: 'Evaluate total loan exposure for high-risk segments. Consider tightening approval criteria for new applications.',
            type: 'account_update' as const,
          },
          {
            id: 'step-launch-intervention',
            title: 'Launch Hardship Intervention',
            description: 'Contact affected members with payment relief options, financial counseling referrals, or restructuring offers.',
            type: 'email' as const,
          },
        ],
      };
    },
  },

  // ── Rule 2: Declining VeraScore (CoreMath signal: score_trend_90d) ────
  {
    key: 'DECLINING_SCORE_90D',
    condition: (b) => b.declining_score_90d_count > 0,
    priorityWeight: PRIORITY_WEIGHTS.high,
    build: (b) => {
      const count = b.declining_score_90d_count;
      return {
        id: 'behavioral-declining-score-90d',
        title: 'Address Members with Declining Financial Health',
        description:
          `${count} member${count !== 1 ? 's' : ''} experienced a significant VeraScore decline ` +
          `(≥15 points) over the past 90 days. This signals deteriorating financial health — ` +
          'consider proactive outreach, financial wellness resources, or early intervention programs.',
        impact: {
          type: 'risk' as const,
          value: count,
          description: `${count} member${count !== 1 ? 's' : ''} with declining scores`,
        },
        priority: 'high' as const,
        category: 'Risk Management',
        applicableRoles: ['CEO', 'VP of Lending', 'Loan Advisor'] as const,
        actionType: 'semi-automated' as const,
        dataSources: ['VS CoreMath — VeraScore Trend Analysis (90-day)'],
        steps: [
          {
            id: 'step-review-declining-members',
            title: 'Review Declining Score Trends',
            description: 'Open VS Insights → Financial Health to review score distribution and trend data for at-risk segments.',
            type: 'loan_review' as const,
          },
          {
            id: 'step-assess-risk-exposure',
            title: 'Assess Portfolio Risk Exposure',
            description: 'Evaluate total loan exposure for members with declining scores. Identify common factors driving the decline.',
            type: 'account_update' as const,
          },
          {
            id: 'step-initiate-outreach',
            title: 'Initiate Early Intervention',
            description: 'Contact affected members with financial wellness resources, payment flexibility options, or counseling referrals before delinquency occurs.',
            type: 'email' as const,
          },
        ],
      };
    },
  },

  // ── Rule 3: High DTI Stress (CoreMath signal: dti_ratio > 40%) ──────────
  {
    key: 'HIGH_DTI_STRESS',
    condition: (b) => b.dti_high_count > 0,
    priorityWeight: PRIORITY_WEIGHTS.high,
    build: (b) => {
      const count = b.dti_high_count;
      return {
        id: 'behavioral-high-dti-stress',
        title: 'Address Members with High DTI Stress',
        description:
          'Members show elevated debt-to-income pressure that may increase repayment risk.',
        impact: {
          type: 'risk' as const,
          value: count,
          description: `${count} member${count !== 1 ? 's' : ''} in high DTI segment`,
        },
        priority: 'high' as const,
        category: 'Debt Management / Risk Management',
        applicableRoles: ['CEO', 'VP of Lending', 'Loan Advisor'] as const,
        actionType: 'semi-automated' as const,
        dataSources: ['VS CoreMath — Debt-to-Income Ratio Analysis'],
        steps: [
          {
            id: 'step-review-dti-patterns',
            title: 'Review Aggregate DTI Pressure Patterns',
            description: 'Open VS Insights → Financial Health to review aggregate DTI pressure patterns across the portfolio.',
            type: 'loan_review' as const,
          },
          {
            id: 'step-prioritize-outreach',
            title: 'Prioritize Outreach or Counseling',
            description: 'Prioritize outreach or counseling for the affected cohort based on DTI severity and portfolio exposure.',
            type: 'account_update' as const,
          },
          {
            id: 'step-support-options',
            title: 'Consider Refinancing & Debt-Management Support',
            description: 'Consider refinancing, consolidation, or debt-management support options for members with elevated DTI.',
            type: 'email' as const,
          },
        ],
      };
    },
  },

  // ── Rule 4: Low Liquid Reserves (single-signal — CoreMath) ────────────
  {
    key: 'LOW_LIQUID_RESERVES',
    condition: (b) => b.liquidity_months_critical_count > 0,
    priorityWeight: PRIORITY_WEIGHTS.high,
    build: (b) => {
      const count = b.liquidity_months_critical_count;
      return {
        id: 'behavioral-low-liquid-reserves',
        title: 'Support Members with Low Liquid Reserves',
        description:
          'Members have less than 1 month of liquid reserves, leaving them vulnerable to unexpected expenses or income disruptions.',
        impact: {
          type: 'risk' as const,
          value: count,
          description: `${count} member${count !== 1 ? 's' : ''} with critically low reserves`,
        },
        priority: 'high' as const,
        category: 'Financial Resilience / Risk Management',
        applicableRoles: ['CEO', 'VP of Lending', 'Loan Advisor'] as const,
        actionType: 'semi-automated' as const,
        dataSources: ['VS CoreMath — Liquid Reserves Analysis'],
        steps: [
          {
            id: 'step-review-reserves',
            title: 'Review Aggregate Reserves Patterns',
            description: 'Open VS Insights → Financial Health to review aggregate liquid reserves patterns across the portfolio.',
            type: 'loan_review' as const,
          },
          {
            id: 'step-prioritize-outreach',
            title: 'Prioritize Outreach for At-Risk Members',
            description: 'Prioritize outreach for the affected cohort based on reserves severity and portfolio exposure.',
            type: 'account_update' as const,
          },
          {
            id: 'step-savings-programs',
            title: 'Offer Savings & Emergency Fund Programs',
            description: 'Consider savings programs, emergency fund products, or financial counseling for members with critically low reserves.',
            type: 'email' as const,
          },
        ],
      };
    },
  },

  // ── Rule 5: High Financial Stress Support (single-signal) ──────────────
  {
    key: 'HIGH_FINANCIAL_STRESS_SUPPORT',
    condition: (b) => b.high_financial_stress_count > 0,
    priorityWeight: PRIORITY_WEIGHTS.high,
    build: (b) => {
      const count = b.high_financial_stress_count;
      return {
        id: 'behavioral-high-financial-stress-support',
        title: 'Support High-Stress Members',
        description:
          `Survey responses indicate elevated financial stress among ${count} member${count !== 1 ? 's' : ''}. ` +
          'Consider offering financial counseling resources or hardship support programs.',
        impact: {
          type: 'risk',
          value: count,
          description: `${count} member${count !== 1 ? 's' : ''} affected`,
        },
        priority: 'high' as const,
        category: 'Financial Wellness',
        applicableRoles: ['CEO', 'VP of Lending', 'Loan Advisor'],
        actionType: 'semi-automated' as const,
        dataSources: ['VS Insights — Live Survey Data'],
        steps: [
          {
            id: 'step-review-stress-insights',
            title: 'Review Financial Stress Insights',
            description: 'Open VS Insights → Financial Health to review the High Financial Stress KPI and trends.',
            type: 'loan_review' as const,
          },
          {
            id: 'step-evaluate-support-options',
            title: 'Evaluate Support Options',
            description: 'Identify appropriate counseling resources, hardship programs, or outreach campaigns.',
            type: 'account_update' as const,
          },
          {
            id: 'step-initiate-outreach',
            title: 'Initiate Member Outreach',
            description: 'Launch targeted outreach to affected members with available support resources.',
            type: 'email' as const,
          },
        ],
      };
    },
  },
];

// ─── P3.3: Performance Boost + Visibility Guardrails ─────────────────────────

/** P3.3: Deterministic performance boost applied to sort weight */
const PERFORMANCE_BOOST: Record<string, number> = {
  HIGH_PERFORMING: 20,
  MEDIUM: 5,
  LOW: -10,
  INSUFFICIENT: 0,
};

/** P3.3: Visibility guardrail thresholds */
const VISIBILITY_MIN_SHOWN = 20;
const VISIBILITY_MIN_CTR = 0.05;

/** P3.3: Map classification → human-readable performance label */
function getPerformanceLabel(classification: string | undefined): string {
  switch (classification) {
    case 'HIGH_PERFORMING':
      return 'High-performing recommendation';
    case 'MEDIUM':
      return 'Moderate engagement';
    case 'LOW':
      return 'Low engagement — being evaluated';
    case 'INSUFFICIENT':
    default:
      return 'New recommendation — learning in progress';
  }
}

/** P3.3+P4C: Map rule key → human-readable trigger reason */
function getTriggerReason(ruleKey: string, behavioral: BehavioralInsights): string {
  switch (ruleKey) {
    case 'HIGH_RISK_INTERVENTION':
      return `${behavioral.high_financial_stress_count} members with high stress + ${behavioral.dti_high_count} with high DTI`;
    case 'DECLINING_SCORE_90D':
      return `${behavioral.declining_score_90d_count} member${behavioral.declining_score_90d_count !== 1 ? 's' : ''} declined ≥15 VeraScore points in the last 90 days`;
    case 'HIGH_DTI_STRESS':
      return `${behavioral.dti_high_count} members are in the high DTI segment`;
    case 'LOW_LIQUID_RESERVES':
      return `${behavioral.liquidity_months_critical_count} member${behavioral.liquidity_months_critical_count !== 1 ? 's' : ''} with less than 1 month of liquid reserves`;
    case 'HIGH_FINANCIAL_STRESS_SUPPORT':
      return `${behavioral.high_financial_stress_count} members reporting elevated financial stress`;
    default:
      return 'Data signal detected';
  }
}

/**
 * P3 Rule Engine v1 + P3.3 Optimization: Generate data-driven recommendations
 * from behavioral aggregate data, with performance-based ranking and visibility.
 *
 * P3.3 additions:
 *   - Performance boost: adjusts sort weight by classification
 *   - Visibility guardrail: hides recs with total_shown ≥ 20 AND ctr < 0.05
 *   - Explainability: adds triggerReason + performanceLabel to each action
 *
 * Privacy: Only aggregate counts are used. No member IDs, no raw survey
 * answers, no individual stress scores.
 */
export const getBehavioralRecommendations = (
  behavioral: BehavioralInsights | undefined,
  performanceMap?: Map<string, { classification: string; total_shown: number; ctr: number }>,
): Action[] => {
  if (!behavioral) return [];

  const fired: { action: Action; weight: number }[] = [];
  for (const rule of RECOMMENDATION_RULES) {
    if (rule.condition(behavioral)) {
      const action = rule.build(behavioral);

      // P3.3: Look up performance data by action ID
      const perf = performanceMap?.get(action.id);
      const classification = perf?.classification ?? 'INSUFFICIENT';
      const boost = PERFORMANCE_BOOST[classification] ?? 0;
      const baseWeight = rule.priorityWeight;

      // P3.3: Visibility guardrail — hide only if enough data AND very low CTR
      if (
        perf &&
        classification !== 'INSUFFICIENT' &&
        perf.total_shown >= VISIBILITY_MIN_SHOWN &&
        perf.ctr < VISIBILITY_MIN_CTR
      ) {
        if (import.meta.env.DEV) {
          console.log(`[P3.3] Hiding ${action.id}: shown=${perf.total_shown}, ctr=${perf.ctr}`);
        }
        continue; // Skip this recommendation
      }

      // P3.3: Add explainability fields
      action.triggerReason = getTriggerReason(rule.key, behavioral);
      action.performanceLabel = getPerformanceLabel(classification);

      // P4A: Attach segment key for outcome tracking
      action.segmentKey = RULE_SEGMENT_KEY_MAP[rule.key];

      fired.push({ action, weight: baseWeight + boost });
    }
  }

  // Sort by boosted weight descending (critical + boost first)
  fired.sort((a, b) => b.weight - a.weight);

  return fired.map((f) => f.action);
};

/**
 * Find members with English names that can be used for new actions
 */
export const findAvailableEnglishNamedMembers = (
  members: Member[],
  financialData: Record<string, FinancialHealthStats[]>,
  usedMemberIds: Set<string>
): Member[] => {
  return members.filter(member => {
    // Must have financial data
    const hasFiData = financialData[member.uuid] && financialData[member.uuid].length > 0;
    
    // Must not have been used already
    const isUnused = !usedMemberIds.has(member.uuid);
    
    // Must have English name (same filter as in actionUtils.ts)
    const nameChars = ((member.first_name || '') + (member.last_name || '')).toLowerCase();
    const hasEnglishName = /^[a-z\s\-'\.]+$/i.test(nameChars);
    
    return hasFiData && isUnused && hasEnglishName;
  });
}; 